import { getType } from '@/modules/editor/region-types.js';

//
// Given an iframe element, this function will disable interactions that can cause the page to
// reload or redirect.
//
//  iframe* (object) - The target iframe.
//
// No return value.
//
export function disableInteractions(iframe) {
  const doc = iframe.contentWindow.document;

  // Prevent clicks on links and image maps
  [...doc.querySelectorAll('a, area')].map(element => {
    if (!element.closest('.cms-editable')) {
      element.addEventListener('click', event => event.preventDefault());
    }
  });

  // Prevent labels from assigning focus to inputs
  [...doc.querySelectorAll('label[for]')].map(element => {
    element.addEventListener('click', event => event.preventDefault());
  });

  // Make inputs readonly
  [...doc.querySelectorAll('input, textarea')].map(element => {
    if (!element.closest('.cms-editable')) {
      element.addEventListener('focus', event => event.target.blur());
    }
  });

  // Prevent form submissions
  [...doc.querySelectorAll('form')].map(element => {
    if (!element.closest('.cms-editable')) {
      element.addEventListener('submit', event => event.preventDefault());
    }
  });
}

//
// Given an iframe element, this function will remove content regions that are located inside other
// content regions. Since nested regions are already editable, the inner editable classes are
// redundant and can cause problems when the editor initializes.
//
//  iframe* (object) - The target iframe.
//
// No return value.
//
export function repairNestedRegions(iframe) {
  const doc = iframe.contentWindow.document;

  // Remove nested content regions
  [...doc.querySelectorAll('.cms-editable')].map(element => {
    const type = getType(element);

    // Exception: nested content regions are allowed inside of background regions!
    if (type !== 'background') {
      [...element.querySelectorAll('.cms-editable')].map(nestedElement => {
        nestedElement.classList.remove('cms-editable');
      });
    }
  });
}

//
// Given an iframe element, this function will look for all legacy classes and shim them with the
// correct cms-* alternative.
//
//  iframe* (object) - The target iframe.
//
// No return value.
//
export function shimLegacyClasses(iframe) {
  const doc = iframe.contentWindow.document;

  // Shim legacy editable classes
  [...doc.querySelectorAll('.editable, .cushycms, .cushycms-wysiwyg, .clienteditor, .clienteditor-wysiwyg')].map(
    element => {
      element.classList.add('cms-editable');
    }
  );

  // Shim legacy gallery classes
  [...doc.querySelectorAll('.editable-gallery')].map(element => {
    element.classList.add('cms-editable');
    element.setAttribute('data-type', 'gallery');
  });

  // Shim legacy editable text classes
  [...doc.querySelectorAll('.editable-text, .cushycms-text, .clienteditor-text, .cms-editable-text')].map(element => {
    element.classList.add('cms-editable');
    element.setAttribute('data-type', 'text');
  });

  // Shim legacy repeatable classes
  [...doc.querySelectorAll('.repeatable')].map(element => {
    element.classList.add('cms-repeatable');
  });

  // Shim legacy uneditable classes
  [...doc.querySelectorAll('.uneditable')].map(element => {
    element.classList.add('cms-uneditable');
  });

  // Shim legacy embeds
  [...doc.querySelectorAll('[data-embed]')].map(element => {
    element.classList.add('cms-embed');
  });
}

//
// Given an iframe and a document object, this function will watch for clicks on frameDoc and emit a
// series of mouse events on the parentDoc to simulate a click. This lets us hide dropdowns, panels
// and other UI components in the main document when the click only registers on the frame.
//
//  iframe* (object) - The target iframe.
//  parentDoc* (object) - The parent document to simulate clicks on.
//
// No return value.
//
export function simulateClicks(iframe, parentDoc) {
  const doc = iframe.contentWindow.document;

  doc.addEventListener('click', () => {
    ['mousedown', 'mouseup', 'click'].map(eventType => {
      const event = parentDoc.createEvent('MouseEvents');
      event.initEvent(eventType, true, true);
      parentDoc.body.dispatchEvent(event);
    });
  });
}
